// AutomationPipeline.jsx — real-time visualization of the auto-posting
// pipeline. Polls /worker/pipeline/status every 3s and renders a 5-stage
// flow (Ideas → Posts → Images → Approval → Publishing) with counts,
// next-slot ETA per brand, and Pause / Resume / Speedup controls.
//
// Animations: count badges pulse when they change; a stage card pulses
// when its in-flight count grows. Lightweight CSS — no extra deps.

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { get, post } from "../../api";
import RunFlows from "./RunFlows";
import AllPipelines from "./AllPipelines";
import FlowLanes from "./FlowLanes";
import EmailJourney from "./EmailJourney";
import PlanningTab from "./PlanningTab";
import { fieldSetter } from "../../helpers/formEvents";

const STAGES = [
  { key: 'idea-generation',  label: 'Ideas',     desc: 'LLM-drafted post ideas', queueKey: 'idea',  countKey: null },
  { key: 'post-generation',  label: 'Drafts',    desc: 'Per-platform post text', queueKey: 'post',  countKey: 'post-generation' },
  { key: 'image-generation', label: 'Images',    desc: 'AI image generation',    queueKey: 'image', countKey: 'image-generation' },
  { key: 'email-approval',   label: 'Approval',  desc: 'Auto-approve / email',   queueKey: 'email', countKey: 'email-approval' },
  { key: 'social-posting',   label: 'Posting',   desc: 'Publishing to platforms',queueKey: 'social',countKey: 'social-posting' },
  { key: 'posted',           label: 'Posted',    desc: 'Live on social',         queueKey: null,    countKey: 'posted' },
];

function fmtSlot(slot) {
  if (!slot) return '—';
  return `${slot.name} (${slot.hourLocal}:00 ${slot.tz})`;
}

function fmtTime(t) {
  if (!t) return '';
  try { return new Date(t).toLocaleTimeString(); } catch { return ''; }
}

function StageCard({ stage, counts, postCount, recentCount, pulse }) {
  const q = counts && counts.queueCounts && stage.queueKey ? counts.queueCounts[stage.queueKey] : null;
  const ps = counts && counts.stagePostCounts && stage.countKey ? counts.stagePostCounts[stage.countKey] : null;
  const inFlight = (q ? ((q.waiting || 0) + (q.active || 0) + (q.delayed || 0)) : 0) + (ps || 0);
  return (
    <div className={"automation-stage " + (pulse ? "pulse" : "")}>
      <div className="stage-label">{stage.label}</div>
      <div className="stage-count">{inFlight}</div>
      <div className="stage-desc">{stage.desc}</div>
      {q && (
        <div className="stage-queue">
          <span title="Queued + active in BullMQ">Q: {(q.waiting||0)+(q.active||0)}</span>
          {q.failed > 0 && <span className="stage-failed" title="Failed jobs (auto-cleaned every 10min)">⚠ {q.failed}</span>}
        </div>
      )}
    </div>
  );
}

function StageArrow({ active }) {
  // When `active` is true (an item transitioned this stage→next in
  // the last poll), animate a small dot traveling along the arrow.
  return (
    <div className="stage-arrow">
      <div className="arrow-track">
        <div className="arrow-line">→</div>
        {active && <div className="arrow-dot" />}
      </div>
    </div>
  );
}

// Two-step selector: pick a Company first, then the Brand list narrows
// to that tenant. Super-admins manage 50+ brands across many companies
// and the original flat list was unusable. The "All companies" /
// "All brands" sentinel values map to onSelect(null) which preserves
// the existing aggregate-view contract downstream.
function BrandSelector({ brands, selected, onSelect }) {
  const selectedBrand = brands.find((b) => b._id === selected) || null;
  const [companyId, setCompanyId] = React.useState(selectedBrand?.companyId || '');

  // Build the company list once per brand-payload — sort alphabetically
  // so the order is stable across polls.
  const companies = React.useMemo(() => {
    const map = new Map();
    for (const b of brands) {
      const id = b.companyId || '_none';
      const name = b.companyName || 'No company';
      if (!map.has(id)) map.set(id, { id, name });
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [brands]);

  const brandsForCompany = React.useMemo(() => {
    if (!companyId) return brands;
    return brands.filter((b) => (b.companyId || '_none') === companyId);
  }, [brands, companyId]);

  // Clear the brand selection when the company changes to something
  // that doesn't contain the previously-selected brand.
  React.useEffect(() => {
    if (!selected) return;
    if (!brandsForCompany.some((b) => b._id === selected)) onSelect(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <select
        className="form-control"
        value={companyId}
        onChange={(e) => setCompanyId(e.target.value)}
        style={{ maxWidth: 220 }}
      >
        <option value="">— All companies —</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
      <select
        className="form-control"
        value={selected || ''}
        onChange={(e) => onSelect(e.target.value || null)}
        style={{ maxWidth: 280 }}
        disabled={brandsForCompany.length === 0}
      >
        <option value="">
          {companyId ? '— All brands in this company —' : '— All brands (aggregate) —'}
        </option>
        {brandsForCompany.map((b) => (
          <option key={b._id} value={b._id}>
            {b.name} {b.autopost === false ? '⏸' : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function AutomationPipeline() {
  const [brandId, setBrandId] = useState(null);
  const isSuperAdmin = useSelector((s) => !!s.user.userInfo?.superAdmin);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [speedupCount, setSpeedupCount] = useState(1);
  const prevCountsRef = useRef({});
  const [pulses, setPulses] = useState({});
  // Tab state — 'pipeline' shows the post-publishing 5-stage flow
  // (the original page); 'queues' shows every queue's BullMQ counts;
  // 'schedulers' lists every scheduler with its cadence + last run.
  // Initial tab can be deep-linked via ?tab=schedulers so the super-
  // admin settings page can drop the user directly on the controls
  // instead of forcing a click through the Pipeline tab first.
  const initialTab = (() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const t = sp.get('tab');
      if (t === 'queues' || t === 'schedulers' || t === 'pipeline' || t === 'run' || t === 'planning') return t;
    } catch { /* SSR / older browsers */ }
    // Default super-admins to Run flows; everyone else to the post pipeline.
    return isSuperAdmin ? 'run' : 'pipeline';
  })();
  const [topTab, setTopTab] = useState(initialTab);
  const [overview, setOverview] = useState(null);
  // AI-ops overview (companies + per-brand modes + global mode) lives here in
  // the parent so the Run-Flows dropdowns survive tab switches.
  const [aiOverview, setAiOverview] = useState(null);
  const fetchAiOverview = useCallback(async () => {
    try { const r = await get('/api/admin/ai-ops/overview'); setAiOverview((r && r.success) ? r : (r && r.data) || r); } catch (_) { /* */ }
  }, []);
  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchAiOverview();
    const id = setInterval(fetchAiOverview, 8000);
    return () => clearInterval(id);
  }, [isSuperAdmin, fetchAiOverview]);
  // Lazy-load the broader operations view from /worker/admin/overview
  // — same endpoint that powers the AdminCostControl page so we don't
  // re-implement the queue inventory + scheduler list.
  useEffect(() => {
    // Loaded for every tab now — the pipeline tab's flow lanes also need
    // the live queue counts from this endpoint.
    let cancelled = false;
    (async () => {
      try {
        const r = await get('/worker/admin/overview');
        if (!cancelled) setOverview((r && r.data) || r);
      } catch (_) { /* surfaces via error state from the pipeline fetch */ }
    })();
    const id = setInterval(async () => {
      try {
        const r = await get('/worker/admin/overview');
        if (!cancelled) setOverview((r && r.data) || r);
      } catch (_) {}
    }, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [topTab]);
  // Per-arrow "active" flags — true for ~1.2s after we detect an
  // item moved from this stage to the next. Drives the animated
  // travelling-dot on the arrow.
  const [arrowActive, setArrowActive] = useState({});

  const fetchStatus = useCallback(async () => {
    try {
      const qs = brandId ? `?brandId=${encodeURIComponent(brandId)}` : '';
      const res = await get(`/worker/pipeline/status${qs}`);
      const d = res && res.data ? res.data : res;
      setData(d);
      // When unscoped, this carries the FULL brand roster — remember it so the
      // selectors/lists don't collapse to one brand after a brand is picked.
      if (!brandId && d && Array.isArray(d.brands)) setAllBrands(d.brands);
      setError(null);
    } catch (e) {
      setError((e && e.message) || 'Failed to fetch pipeline status');
    }
  }, [brandId]);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 3000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  // The full brand roster, fetched UNSCOPED so it stays complete regardless of
  // which brand is selected. The scoped `data` above drives the per-brand
  // pipeline view; this drives every brand/company selector. Refreshed every
  // 30s so newly-created brands appear without clearing the selection.
  const [allBrands, setAllBrands] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const loadRoster = async () => {
      try {
        const r = await get('/worker/pipeline/status');
        const d = (r && r.data) || r;
        if (!cancelled && d && Array.isArray(d.brands)) setAllBrands(d.brands);
      } catch (_) { /* selector falls back to scoped data */ }
    };
    loadRoster();
    const id = setInterval(loadRoster, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Detect count changes to pulse stage cards AND to flag arrows
  // when an item moved into a stage (count climbed). The arrow
  // before a growing stage animates a travelling dot for ~1.2s.
  useEffect(() => {
    if (!data || !data.counts) return;
    const newPulses = {};
    const newArrows = {};
    const prev = prevCountsRef.current;
    for (let i = 0; i < STAGES.length; i++) {
      const stage = STAGES[i];
      const q = data.counts.queueCounts && stage.queueKey ? data.counts.queueCounts[stage.queueKey] : null;
      const ps = data.counts.stagePostCounts && stage.countKey ? data.counts.stagePostCounts[stage.countKey] : null;
      const cur = (q ? ((q.waiting||0)+(q.active||0)) : 0) + (ps || 0);
      const was = prev[stage.key] || 0;
      if (cur > was) {
        newPulses[stage.key] = Date.now();
        // Activate the arrow leading INTO this stage (the one
        // between STAGES[i-1] and STAGES[i]).
        if (i > 0) newArrows[STAGES[i - 1].key] = Date.now();
      }
      prev[stage.key] = cur;
    }
    if (Object.keys(newPulses).length || Object.keys(newArrows).length) {
      setPulses((p) => ({ ...p, ...newPulses }));
      setArrowActive((a) => ({ ...a, ...newArrows }));
      const timeoutId = setTimeout(() => {
        setPulses({});
        setArrowActive({});
      }, 1500);
      return () => clearTimeout(timeoutId);
    }
  }, [data]);

  const cancelItem = useCallback(async (kind, id) => {
    try {
      await post(`/worker/pipeline/control/cancel/${kind}/${id}`, {}, false);
      setActionMsg(`✓ ${kind} cancelled`);
      setTimeout(() => setActionMsg(null), 3000);
      fetchStatus();
    } catch (e) {
      setActionMsg(`✗ cancel failed: ${(e && e.message) || ''}`);
    }
  }, [fetchStatus]);

  const doAction = async (action) => {
    setActionMsg(null);
    try {
      let url;
      if (action === 'pause')   url = `/worker/pipeline/control/pause/${brandId}`;
      if (action === 'resume')  url = `/worker/pipeline/control/resume/${brandId}`;
      if (action === 'speedup') url = `/worker/pipeline/control/speedup/${brandId}`;
      if (!url) return;
      const body = action === 'speedup'
        ? { count: Math.max(1, Math.min(5, parseInt(speedupCount, 10) || 1)) }
        : {};
      await post(url, body, false);
      setActionMsg(`✓ ${action}${action === 'speedup' ? ` (x${body.count})` : ''} fired`);
      setTimeout(() => setActionMsg(null), 3000);
      fetchStatus();
    } catch (e) {
      setActionMsg(`✗ ${(e && e.message) || 'action failed'}`);
    }
  };

  const delayPost = useCallback(async (postId, minutes) => {
    try {
      await post(`/worker/pipeline/control/delay/${postId}`, { minutes }, false);
      setActionMsg(`✓ delayed ${minutes}min`);
      setTimeout(() => setActionMsg(null), 3000);
      fetchStatus();
    } catch (e) {
      setActionMsg(`✗ delay failed: ${(e && e.message) || ''}`);
    }
  }, [fetchStatus]);

  const isBrandView = !!brandId;
  // Selectors/lists use the full unscoped roster; only fall back to the
  // (possibly brand-scoped) pipeline payload before the roster has loaded.
  const aggregateBrands = useMemo(
    () => (allBrands.length ? allBrands : ((data && data.brands) || [])),
    [allBrands, data]
  );
  const currentBrand = data && data.brand;
  const counts = data && data.counts;

  return (
    <div className="container py-4">
      <style>{`
        .automation-flow { display: flex; align-items: stretch; gap: 8px; flex-wrap: nowrap; overflow-x: auto; padding: 16px 0; }
        .automation-stage { flex: 1 1 0; min-width: 140px; background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: 14px; text-align: center; transition: transform .25s, box-shadow .25s; }
        .automation-stage.pulse { transform: scale(1.04); box-shadow: 0 6px 18px rgba(15,98,254,.18); border-color: #0F62FE; }
        .stage-label { font-size: 12px; color: #525252; text-transform: uppercase; letter-spacing: .5px; }
        .stage-count { font-size: 32px; font-weight: 700; color: #15172b; margin: 4px 0; }
        .stage-desc { font-size: 11px; color: #a3a3a3; }
        .stage-queue { font-size: 11px; color: #555; margin-top: 6px; display: flex; gap: 8px; justify-content: center; }
        .stage-failed { color: #c0392b; }
        .stage-arrow { display: flex; align-items: center; font-size: 24px; color: #ccc; padding: 0 4px; }
        .arrow-track { position: relative; width: 40px; height: 24px; display: flex; align-items: center; justify-content: center; }
        .arrow-line { position: relative; z-index: 1; }
        @keyframes arrow-travel { from { left: -4px; opacity: 0; } 30% { opacity: 1; } 70% { opacity: 1; } to { left: 40px; opacity: 0; } }
        .arrow-dot { position: absolute; top: 50%; left: -4px; width: 8px; height: 8px; border-radius: 50%; background: #0F62FE; transform: translateY(-50%); animation: arrow-travel 1.2s ease-in-out; }
        .brand-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
        .next-slot { font-size: 13px; color: #555; }
        .next-slot strong { color: #15172b; }
        .pip-actions button { margin-right: 8px; }
        .recent-list { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 16px; }
        .recent-list h6 { font-size: 13px; color: #555; margin-bottom: 6px; }
        .recent-list .item { font-size: 12px; padding: 4px 8px; border-bottom: 1px solid #f1f1f1; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
        .recent-list .item .qs { color: #888; }
        .recent-list .item .cancel-btn { background: transparent; border: 1px solid #e5e5e5; border-radius: 4px; padding: 0 6px; font-size: 11px; cursor: pointer; color: #c0392b; }
        .recent-list .item .cancel-btn:hover { background: #fdecea; }
        .recent-list .item .delay-btn { background: transparent; border: 1px solid #e5e5e5; border-radius: 4px; padding: 0 6px; font-size: 11px; cursor: pointer; color: #525252; }
        .recent-list .item .delay-btn:hover { background: #f4f4f4; }
        .recent-list .item.cancelled { opacity: .5; text-decoration: line-through; }
        .brand-list { margin-top: 24px; }
        .brand-list .row { display: flex; align-items: center; gap: 10px; padding: 6px 0; border-bottom: 1px solid #f4f4f4; font-size: 13px; }
        .brand-list .row .name { flex: 1; font-weight: 500; cursor: pointer; color: #0F62FE; }
        .brand-list .row .meta { font-size: 11px; color: #888; }
        .badge-paused { background: #f1f1f1; color: #525252; border-radius: 10px; padding: 1px 8px; font-size: 11px; }
      `}</style>

      <h3>Automation Pipeline</h3>
      <p className="text-muted" style={{ fontSize: 13 }}>
        Real-time visibility into idea → draft → image → approval → posting.
        Auto-refreshes every 3 seconds.
      </p>

      <div className="brand-row">
        <label style={{ fontSize: 13, color: '#555' }}>Brand:</label>
        <BrandSelector brands={aggregateBrands} selected={brandId} onSelect={setBrandId} />
        {currentBrand && (
          <div className="next-slot">
            Next slot: <strong>{fmtSlot(data.nextSlot)}</strong>
            {' · '}timezone: <strong>{currentBrand.timezone}</strong>
            {' · '}autopost: <strong>{currentBrand.autopost ? 'on' : 'off'}</strong>
          </div>
        )}
        {actionMsg && <span style={{ fontSize: 12, color: '#0F62FE' }}>{actionMsg}</span>}
      </div>

      {error && (
        <div className="alert alert-warning" style={{ fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Top-level tabs: Pipeline (the original 5-stage post flow),
          All queues (every BullMQ queue with job counts), Schedulers
          (every periodic worker with cadence + stats). */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e5e5', marginBottom: 16 }}>
        {[...(isSuperAdmin ? ['run'] : []), 'pipeline', 'queues', 'schedulers', ...(isSuperAdmin ? ['planning'] : [])].map((k) => (
          <button
            key={k}
            onClick={() => setTopTab(k)}
            style={{
              padding: '8px 14px', background: 'none', border: 'none',
              borderBottom: topTab === k ? '2px solid #15172b' : '2px solid transparent',
              fontWeight: topTab === k ? 600 : 400, fontSize: 13, cursor: 'pointer',
            }}
          >
            {k === 'pipeline' ? 'All Pipelines' : k === 'queues' ? 'All queues' : k === 'schedulers' ? 'Schedulers' : k === 'run' ? '▶ Run flows' : 'Planning'}
          </button>
        ))}
      </div>

      {topTab === 'queues' && (
        <QueuesTab queues={overview && overview.queues} />
      )}
      {topTab === 'schedulers' && (
        <SchedulersTab schedulers={overview && overview.schedulers} />
      )}
      {topTab === 'run' && isSuperAdmin && (
        <RunFlows companies={(aiOverview && aiOverview.companies) || []} globalMode={(aiOverview && aiOverview.globalMode) || 'normal'} refreshOverview={fetchAiOverview} refreshPipeline={fetchStatus} liveSchedulers={overview && overview.schedulers} />
      )}
      {topTab === 'planning' && isSuperAdmin && (
        <PlanningTab brands={aggregateBrands} brandId={brandId} onSelectBrand={setBrandId} />
      )}

      {topTab === 'pipeline' && (<>
      {/* All-pipelines per-brand flow projection (super-admin) — every automated
          flow per brand, projected per day/recurrence, with per-brand toggles.
          The classic 5-stage post-pipeline view follows below. */}
      {isSuperAdmin && <AllPipelines />}
      <h6 style={{ fontSize: 13, color: '#555', marginTop: 24 }}>Post pipeline (live stages)</h6>
      <div className="automation-flow">
        {STAGES.map((s, i) => (
          <React.Fragment key={s.key}>
            <StageCard stage={s} counts={counts} pulse={!!pulses[s.key]} />
            {i < STAGES.length - 1 && <StageArrow active={!!arrowActive[s.key]} />}
          </React.Fragment>
        ))}
      </div>

      {/* Per-flow live lanes (Social / SEO / Email) from the worker queue counts. */}
      <FlowLanes queues={overview && overview.queues} />
      {isSuperAdmin && <EmailJourney brandId={brandId} />}

      {isBrandView && (
        <div className="pip-actions" style={{ marginTop: 12 }}>
          {currentBrand && currentBrand.autopost
            ? <button className="btn btn-warning btn-sm" onClick={() => doAction('pause')}>Pause autopost</button>
            : <button className="btn btn-success btn-sm" onClick={() => doAction('resume')}>Resume autopost</button>}
          <input
            type="number"
            min="1"
            max="5"
            value={speedupCount}
            onChange={(e) => setSpeedupCount(e.target.value)}
            style={{ width: 50, marginRight: 4, padding: '2px 6px', border: '1px solid #ddd', borderRadius: 4 }}
            title="How many ideas to enqueue immediately (1-5)"
          />
          <button className="btn btn-primary btn-sm" onClick={() => doAction('speedup')}>
            Force {speedupCount} idea{speedupCount === 1 || speedupCount === '1' ? '' : 's'}
          </button>
        </div>
      )}

      {isBrandView && data && data.recent && (
        <div className="recent-list">
          <div>
            <h6>Recent Ideas (24h, top 50)</h6>
            {(data.recent.ideas || []).length === 0 && <div style={{ fontSize: 12, color: '#999' }}>None.</div>}
            {(data.recent.ideas || []).map((i) => {
              const isCancellable = !['posted', 'cancelled'].includes(i.queueStatus);
              return (
                <div key={i._id} className={"item" + (i.queueStatus === 'cancelled' ? ' cancelled' : '')}>
                  <span>{i.title || '(untitled)'} <span className="qs">{fmtTime(i.createdAt)}</span></span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="qs">{i.queueStatus}</span>
                    {isCancellable && (
                      <button className="cancel-btn" title="Cancel this idea" onClick={() => cancelItem('idea', i._id)}>✕</button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <div>
            <h6>Recent Posts (24h, top 50)</h6>
            {(data.recent.posts || []).length === 0 && <div style={{ fontSize: 12, color: '#999' }}>None.</div>}
            {(data.recent.posts || []).map((p) => {
              const isCancellable = !['posted', 'partially-posted', 'posting', 'cancelled'].includes(p.queueStatus);
              // Delay only makes sense while the post is still in
              // flight — once posted/posting/cancelled, the scheduled_at
              // change can't intervene.
              const isDelayable = !['posted', 'partially-posted', 'posting', 'cancelled'].includes(p.queueStatus);
              return (
                <div key={p._id} className={"item" + (p.queueStatus === 'cancelled' ? ' cancelled' : '')}>
                  <span>{(p.socialPlatforms || []).join(', ') || '(no platform)'} <span className="qs">{fmtTime(p.createdAt)}</span></span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span className="qs">{p.queueStatus}</span>
                    {isDelayable && (
                      <>
                        <button className="delay-btn" title="Delay 1 hour"  onClick={() => delayPost(p._id, 60)}>+1h</button>
                        <button className="delay-btn" title="Delay 24 hours" onClick={() => delayPost(p._id, 60 * 24)}>+1d</button>
                      </>
                    )}
                    {isCancellable && (
                      <button className="cancel-btn" title="Cancel this post" onClick={() => cancelItem('post', p._id)}>✕</button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!isBrandView && (
        <div className="brand-list">
          <h6 style={{ fontSize: 13, color: '#555' }}>Brands</h6>
          {aggregateBrands.length === 0 && <div style={{ fontSize: 12, color: '#999' }}>No live brands.</div>}
          {aggregateBrands.map((b) => (
            <div key={b._id} className="row">
              <div className="name" onClick={() => setBrandId(b._id)}>{b.name}</div>
              {b.autopost === false && <span className="badge-paused">paused</span>}
              <span className="meta">tz: {b.timezone}</span>
              <span className="meta">next: {fmtSlot(b.nextSlot)}</span>
              <span className="meta">in-flight: <strong>{b.inFlight}</strong></span>
            </div>
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}

// ── All-queues tab: every BullMQ queue with its job counts. Driven by
// /worker/admin/overview's queueInventory (recently expanded to list
// all 30+ queues across post, email, SEO, chat, newsletter, CRM,
// trial reminder).
// Human-readable names + one-line descriptions for every BullMQ queue, so the
// table reads as work types rather than bare "*-queue" ids.
const QUEUE_LABELS = {
  'token-refresh-queue':        ['OAuth token refresh', 'Refreshes expiring social/email tokens'],
  'brand-processing-queue':     ['Brand processing', 'Background brand setup tasks'],
  'brand-enrichment-queue':     ['Brand enrichment', 'Crawls site + social, synthesizes the brand profile'],
  'idea-generation-queue':      ['Idea generation', 'LLM-drafted post ideas'],
  'post-generation-queue':      ['Post generation', 'Per-platform post text'],
  'article-generation-queue':   ['Article generation', 'Long-form article drafting'],
  'image-generation-queue':     ['Image generation', 'AI image creation + watermark'],
  'video-generation-queue':     ['Video generation', 'AI video creation'],
  'email-approval-queue':       ['Email approval', 'Auto-approve / supervisor gate'],
  'social-posting-queue':       ['Social posting', 'Publishing to platforms'],
  'engagement-refresh-queue':   ['Engagement refresh', 'Pulls likes/comments'],
  'comment-refresh-queue':      ['Comment refresh', 'Pulls social comments'],
  'comment-sentiment-queue':    ['Comment sentiment', 'Classifies comment tone'],
  'email-ingestion-queue':      ['Email ingestion', 'Inbound mail intake'],
  'email-classification-queue': ['Email classification', 'Classifies inbound email'],
  'email-reply-queue':          ['Email reply draft', 'Drafts replies to inbound mail'],
  'email-reply-approval-queue': ['Email reply approval', 'Supervisor approval for replies'],
  'email-send-queue':           ['Email send', 'Outbound email delivery'],
  'email-campaign-queue':       ['Email campaign', 'Campaign blast dispatch'],
  'email-sequence-generate-queue': ['Email sequence generation', 'Generates AI drip sequences'],
  'email-sequence-step-queue':  ['Email sequence step', 'Sends due sequence steps'],
  'email-template-generate-queue': ['Email template generation', 'Generates email templates'],
  'seo-brief-queue':            ['SEO brief', 'Outline + keyword brief'],
  'seo-article-queue':          ['SEO article', 'Full article drafting'],
  'seo-faq-queue':              ['SEO FAQ', 'FAQ answer generation'],
  'seo-keyword-research-queue': ['SEO keyword research', 'Discovers target keywords'],
  'seo-publish-queue':          ['SEO publish', 'Publishes articles to portals'],
  'chat-ingestion-queue':       ['Chat ingestion', 'Inbound chat intake'],
  'chat-classification-queue':  ['Chat classification', 'Classifies chat messages'],
  'crm-state-analyze-queue':    ['CRM state analyze', 'Analyzes contact lifecycle state'],
  'alert-notify-queue':         ['Alert notify', 'Operational alert fan-out'],
  'perfectionist-queue':        ['Perfectionist', 'Polishes copy to brand voice'],
};
function queueLabel(key, name) {
  const m = QUEUE_LABELS[key] || QUEUE_LABELS[name];
  return m ? m[0] : (key || name);
}
function queueDesc(key, name) {
  const m = QUEUE_LABELS[key] || QUEUE_LABELS[name];
  return m ? m[1] : '';
}
function QueuesTab({ queues }) {
  if (!queues) return <div style={{ fontSize: 12, color: '#999' }}>Loading queue inventory…</div>;
  const entries = Object.entries(queues || {})
    .map(([key, v]) => ({ key, ...v }))
    .sort((a, b) => ((b.waiting||0)+(b.active||0)+(b.failed||0)) - ((a.waiting||0)+(a.active||0)+(a.failed||0)));
  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
        {entries.length} queues registered. Sorted by activity (waiting + active + failed).
      </div>
      <table className="acc-table" style={{ width: '100%', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #f1f1f1' }}>
            <th style={{ textAlign: 'left', padding: 6, fontSize: 11, color: '#888' }}>Queue</th>
            <th style={{ textAlign: 'right', padding: 6, fontSize: 11, color: '#888' }}>Waiting</th>
            <th style={{ textAlign: 'right', padding: 6, fontSize: 11, color: '#888' }}>Active</th>
            <th style={{ textAlign: 'right', padding: 6, fontSize: 11, color: '#888' }}>Delayed</th>
            <th style={{ textAlign: 'right', padding: 6, fontSize: 11, color: '#888' }}>Completed</th>
            <th style={{ textAlign: 'right', padding: 6, fontSize: 11, color: '#888' }}>Failed</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((q) => (
            <tr key={q.key} style={{ borderBottom: '1px solid #f4f4f4' }}>
              <td style={{ padding: 6, fontSize: 12 }}>
                <strong>{queueLabel(q.key, q.name)}</strong>
                {queueDesc(q.key, q.name) && <div style={{ fontSize: 10, color: '#777' }}>{queueDesc(q.key, q.name)}</div>}
                <div style={{ fontSize: 9, color: '#bbb' }}>{q.name || q.key}</div>
                {q.error && <div style={{ fontSize: 10, color: '#a71d2a' }}>error: {q.error}</div>}
              </td>
              <td style={{ padding: 6, textAlign: 'right', fontSize: 12 }}>{q.waiting || 0}</td>
              <td style={{ padding: 6, textAlign: 'right', fontSize: 12, color: q.active ? '#0F62FE' : '#888' }}>{q.active || 0}</td>
              <td style={{ padding: 6, textAlign: 'right', fontSize: 12, color: '#888' }}>{q.delayed || 0}</td>
              <td style={{ padding: 6, textAlign: 'right', fontSize: 12, color: '#888' }}>{q.completed || 0}</td>
              <td style={{ padding: 6, textAlign: 'right', fontSize: 12, color: q.failed ? '#a71d2a' : '#888' }}>{q.failed || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Schedulers tab: every periodic worker with its cadence + last run.
// Drawn from workerPoolManager.listSchedulers() via /worker/admin/overview.
function SchedulersTab({ schedulers }) {
  // Super-admin only: each scheduler row gets a "Run now" button that
  // POSTs to /worker/api/schedulers/:name/run-now and triggers an
  // immediate tick instead of waiting for the next interval. The proxy
  // already gates this regex to superAdmin (back/routes/workerProxy.js
  // SUPERADMIN_ONLY), but we hide the button entirely for non-supers
  // so it's not misleading.
  const isSuperAdmin = useSelector((s) => !!s.user.userInfo?.superAdmin);
  // Per-row state: 'running' | { ok, summary, error } | null
  const [runState, setRunState] = useState({});
  // Cadence draft per row — keyed by scheduler name. Stored in seconds
  // because that's what users actually think in ("every 30s") even
  // though the worker takes ms. We convert at the boundary.
  const [intervalDraft, setIntervalDraft] = useState({});
  // Last set-interval result per row, same shape as runState.
  const [intervalState, setIntervalState] = useState({});

  if (!schedulers) return <div style={{ fontSize: 12, color: '#999' }}>Loading scheduler list…</div>;
  if (!schedulers.length) return <div style={{ fontSize: 12, color: '#999' }}>No schedulers registered.</div>;
  function fmt(t) { if (!t) return '—'; try { return new Date(t).toLocaleString(); } catch { return String(t); } }
  function fmtIv(ms) {
    if (!ms) return '—';
    const s = Math.round(ms / 1000);
    if (s < 60) return s + 's';
    const m = Math.round(s / 60);
    if (m < 60) return m + 'm';
    const h = Math.round(m / 60);
    if (h < 24) return h + 'h';
    return Math.round(h / 24) + 'd';
  }

  async function runNow(name) {
    setRunState((p) => ({ ...p, [name]: 'running' }));
    try {
      const res = await post(`/worker/api/schedulers/${encodeURIComponent(name)}/run-now`, {});
      // Worker returns { success: true, data: { name, result: { scanned, emitted, skipped, durationMs } | null } }
      const r = res && res.data && res.data.result;
      const summary = r
        ? `scanned ${r.scanned ?? 0}, emitted ${r.emitted ?? 0}, skipped ${r.skipped ?? 0} (${r.durationMs ?? 0}ms)`
        : 'ran (no result payload)';
      setRunState((p) => ({ ...p, [name]: { ok: true, summary } }));
    } catch (e) {
      setRunState((p) => ({ ...p, [name]: { ok: false, error: (e && e.message) || 'Request failed' } }));
    }
  }

  async function saveInterval(name) {
    const seconds = Number(intervalDraft[name]);
    if (!Number.isFinite(seconds) || seconds < 1) {
      setIntervalState((p) => ({ ...p, [name]: { ok: false, error: 'Enter a number ≥ 1 (seconds)' } }));
      return;
    }
    // Worker rejects intervals < 1000ms (per setSchedulerInterval guard),
    // so the minimum on this side is 1 second.
    const intervalMs = Math.round(seconds * 1000);
    setIntervalState((p) => ({ ...p, [name]: 'saving' }));
    try {
      const res = await post(
        `/worker/api/schedulers/${encodeURIComponent(name)}/set-interval`,
        { intervalMs }
      );
      // Worker echoes the new interval back; show it as the success message
      // so the user can verify the change took effect (in-memory only —
      // a worker restart reverts to whatever the env / default is).
      const got = res && res.data && (res.data.intervalMs ?? res.data?.meta?.intervalMs);
      setIntervalState((p) => ({
        ...p,
        [name]: {
          ok: true,
          summary: `interval now ${Math.round((got ?? intervalMs) / 1000)}s (resets on worker restart)`,
        },
      }));
    } catch (e) {
      setIntervalState((p) => ({ ...p, [name]: { ok: false, error: (e && e.message) || 'Save failed' } }));
    }
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
        {schedulers.length} schedulers running. Each ticks on its own interval.
        {isSuperAdmin && ' Use "Run now" to fire an out-of-band tick — useful when you just approved a post manually and don\'t want to wait up to 60s for the next due-post sweep.'}
      </div>
      <table className="acc-table" style={{ width: '100%', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #f1f1f1' }}>
            <th style={{ textAlign: 'left',  padding: 6, fontSize: 11, color: '#888' }}>Scheduler</th>
            <th style={{ textAlign: 'right', padding: 6, fontSize: 11, color: '#888' }}>Cadence</th>
            <th style={{ textAlign: 'right', padding: 6, fontSize: 11, color: '#888' }}>Last run</th>
            <th style={{ textAlign: 'right', padding: 6, fontSize: 11, color: '#888' }}>Scanned</th>
            <th style={{ textAlign: 'right', padding: 6, fontSize: 11, color: '#888' }}>Emitted</th>
            {isSuperAdmin && (
              <th style={{ textAlign: 'right', padding: 6, fontSize: 11, color: '#888' }}>Action</th>
            )}
          </tr>
        </thead>
        <tbody>
          {schedulers.map((s) => {
            const state = runState[s.name];
            const ivState = intervalState[s.name];
            const draft = intervalDraft[s.name];
            const currentSeconds = s.intervalMs ? Math.round(s.intervalMs / 1000) : '';
            return (
              <tr key={s.name} style={{ borderBottom: '1px solid #f4f4f4' }}>
                <td style={{ padding: 6, fontSize: 12 }}>
                  <strong>{s.label || s.name}</strong>
                  {s.description && <div style={{ fontSize: 10, color: '#777' }}>{s.description}</div>}
                  <div style={{ fontSize: 9, color: '#bbb' }}>{s.name}</div>
                </td>
                <td style={{ padding: 6, textAlign: 'right', fontSize: 12 }}>
                  {isSuperAdmin ? (
                    // Inline interval editor. Number-input in seconds
                    // (worker takes ms; conversion lives in saveInterval).
                    // Change resets on worker restart — that's a
                    // deliberate property of WorkerPoolManager.setSchedulerInterval.
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <input
                        type="number"
                        min="1"
                        value={draft ?? currentSeconds}
                        onChange={fieldSetter(setIntervalDraft, s.name)}
                        style={{ width: 56, padding: '2px 4px', border: '1px solid #ddd', borderRadius: 3, fontSize: 11, textAlign: 'right' }}
                        title="Tick interval in seconds. Hot-swap; reverts on worker restart."
                      />
                      <span style={{ fontSize: 10, color: '#888' }}>s</span>
                      <button
                        className="btn btn-sm"
                        onClick={() => saveInterval(s.name)}
                        disabled={ivState === 'saving' || draft === undefined || Number(draft) === currentSeconds}
                        style={{
                          padding: '1px 8px', fontSize: 10, marginLeft: 2,
                          background: '#fff', color: '#15172b',
                          border: '1px solid #15172b', borderRadius: 3,
                          cursor: ivState === 'saving' ? 'wait' : 'pointer',
                          opacity: (draft === undefined || Number(draft) === currentSeconds) ? 0.4 : 1,
                        }}
                      >
                        {ivState === 'saving' ? '…' : 'Save'}
                      </button>
                    </div>
                  ) : fmtIv(s.intervalMs)}
                  {ivState && ivState !== 'saving' && (
                    <div style={{ fontSize: 10, color: ivState.ok ? '#1e6b3e' : '#b91c1c', marginTop: 2 }}>
                      {ivState.ok ? ivState.summary : ivState.error}
                    </div>
                  )}
                </td>
                <td style={{ padding: 6, textAlign: 'right', fontSize: 12, color: '#888' }}>{fmt(s.lastRunAt)}</td>
                <td style={{ padding: 6, textAlign: 'right', fontSize: 12 }}>{s.lastScanned ?? '—'}</td>
                <td style={{ padding: 6, textAlign: 'right', fontSize: 12, color: s.lastEmitted ? '#1e6b3e' : '#888' }}>{s.lastEmitted ?? '—'}</td>
                {isSuperAdmin && (
                  <td style={{ padding: 6, textAlign: 'right', fontSize: 12 }}>
                    <button
                      className="btn btn-sm"
                      onClick={() => runNow(s.name)}
                      disabled={state === 'running'}
                      style={{
                        padding: '2px 10px', fontSize: 11,
                        background: state === 'running' ? '#e5e5e5' : '#15172b',
                        color: state === 'running' ? '#666' : '#fff',
                        border: 'none', borderRadius: 4, cursor: state === 'running' ? 'wait' : 'pointer',
                      }}
                      title={`POST /worker/api/schedulers/${s.name}/run-now`}
                    >
                      {state === 'running' ? 'Running…' : 'Run now'}
                    </button>
                    {state && state !== 'running' && (
                      <div style={{ fontSize: 10, color: state.ok ? '#1e6b3e' : '#b91c1c', marginTop: 2 }}>
                        {state.ok ? state.summary : state.error}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
